import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Icon } from 'antd'
import { ROW_STATUS } from './handoverReducer'

const SOURCE_LABEL = {
    qr: 'QR',
    manual: 'Nhập tay',
    edited: 'Sửa tay',
}

const formatTime = (date) => {
    const m = moment(date)
    return m.isSame(moment(), 'day') ? m.format('HH:mm') : m.format('HH:mm DD/MM')
}

// Dòng lỗi đỏ: lý do + việc cần làm
const describeError = (status, check, duplicateOf) => {
    if (status === ROW_STATUS.DUPLICATE) return `Trùng với dòng #${duplicateOf}. Xoá dòng này.`
    if (status !== ROW_STATUS.ERROR || !check) return ''

    if (check.code === 'already_handed') {
        const { partner_name: partnerName, sewing_at: sewingAt, give_sew_name: giveSewName } = check.handed || {}
        return [
            check.atSubmit ? 'Vừa được giao' : 'Đã được giao',
            partnerName && `cho ${partnerName}`,
            sewingAt && `lúc ${formatTime(sewingAt)}`,
            giveSewName && `bởi ${giveSewName}`,
        ].filter(Boolean).join(' ') + '. Xoá dòng này.'
    }

    if (check.code === 'not_found') return 'Không tìm thấy lô. Sửa tên lô hoặc xoá dòng này.'
    if (check.code === 'invalid_format') return 'Tên lô sai định dạng (VD: R-46906).'

    return check.message || 'Lô không giao được.'
}

const describeMeta = (row, status, check) => {
    const parts = [SOURCE_LABEL[row.source] || 'QR']

    if (status === ROW_STATUS.DECODING) return 'Đang đọc QR...'
    if (status === ROW_STATUS.NO_QR) {
        return row.source === 'manual' ? 'Nhập tay · gõ tên lô trên lệnh may' : 'Không đọc được QR. Nhập tên lô theo ảnh.'
    }
    if (status === ROW_STATUS.NEED_PICK) return 'Ảnh có nhiều mã · chọn 1 lô'
    if (status === ROW_STATUS.CHECKING) return `${parts[0]} · đang kiểm tra...`

    if (row.codes.length > 1 && row.codes.includes(row.name)) parts.push('đã chọn 1 mã')

    if (check && check.sku) parts.push(check.sku)
    if (check && check.items_count) parts.push(`${check.items_count} item`)

    return parts.join(' · ')
}

// Nhãn kèm từng mã của ảnh nhiều mã: lô Chờ may (gợi ý chọn) / lô đã giao.
// Đã chọn 1 mã → chỉ tô mã đang chọn, các mã còn lại để trung tính cho chọn lại.
export const describeCode = (check, { selected = false, picked = false } = {}) => {
    const label = !check || check.pending
        ? ''
        : check.valid ? 'Chờ may' : check.code === 'already_handed' ? 'đã giao' : ''

    if (selected) return { label, tone: 'is-selected' }
    if (picked || !label) return { label, tone: '' }
    return { label, tone: label === 'Chờ may' ? 'is-suggested' : 'is-handed' }
}

const HandoverRow = ({ row, status, check, duplicateOf, codeChecks, disabled, onPreview, onPickCode, onSetName, onRemove }) => {
    const [draft, setDraft] = useState(row.name)
    const inputRef = useRef(null)

    useEffect(() => {
        setDraft(row.name)
    }, [row.name])

    useEffect(() => {
        if (row.autoFocus && inputRef.current) inputRef.current.focus()
    }, [row.autoFocus])

    const _commit = () => {
        const value = draft.trim().toUpperCase()
        if (!value) {
            setDraft(row.name)
            return
        }
        if (value !== row.name) onSetName(row.key, value)
    }

    const error = describeError(status, check, duplicateOf)
    const isOk = status === ROW_STATUS.OK
    const isChecking = status === ROW_STATUS.CHECKING || status === ROW_STATUS.DECODING

    return (
        <div className={`HandoverRow is-${status}`} id={`handover-row-${row.key}`}>
            <span className="HandoverRow-seq">#{row.seq}</span>

            {row.previewUrl
                ? (
                    <button type="button" className="HandoverRow-thumb" onClick={() => onPreview(row.key)} aria-label="Xem ảnh lệnh may">
                        <img src={row.previewUrl} alt="" />
                    </button>
                )
                : (
                    <span className="HandoverRow-thumb is-empty" title="Dòng nhập tay, không có ảnh">
                        <Icon type="edit" />
                    </span>
                )}

            <div className="HandoverRow-main">
                <div className="HandoverRow-line">
                    <input
                        ref={inputRef}
                        className="HandoverRow-name"
                        value={draft}
                        disabled={disabled || status === ROW_STATUS.DECODING}
                        placeholder={status === ROW_STATUS.DECODING ? 'Đang đọc...' : 'Tên lô, vd R-46906'}
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                        onChange={e => setDraft(e.target.value)}
                        onBlur={_commit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') e.target.blur()
                        }}
                        aria-label={`Tên lô dòng ${row.seq}`}
                    />
                    <span className="HandoverRow-state" aria-hidden="true">
                        {isOk && <Icon type="check" className="is-ok-icon" />}
                        {isChecking && <Icon type="loading" />}
                    </span>
                    <button
                        type="button"
                        className="HandoverRow-remove"
                        disabled={disabled}
                        onClick={() => onRemove(row.key)}
                        aria-label={`Xoá dòng ${row.seq}`}
                    >
                        <Icon type="close" />
                    </button>
                </div>

                <div className="HandoverRow-meta">{describeMeta(row, status, check)}</div>

                {row.codes.length > 1 && status !== ROW_STATUS.DECODING && (
                    <div className="HandoverRow-codes">
                        {row.codes.map((code) => {
                            const { label, tone } = describeCode(codeChecks[code], {
                                selected: code === row.name,
                                picked: row.codes.includes(row.name),
                            })
                            return (
                                <button
                                    key={code}
                                    type="button"
                                    className={`HandoverRow-code${tone ? ` ${tone}` : ''}`}
                                    disabled={disabled}
                                    aria-pressed={code === row.name}
                                    onClick={() => code !== row.name && onPickCode(row.key, code)}
                                >
                                    {code}{label ? ` · ${label}` : ''}
                                </button>
                            )
                        })}
                    </div>
                )}

                {error && <div className="HandoverRow-error"><span role="img" aria-label="Lỗi">⛔</span> {error}</div>}
            </div>
        </div>
    )
}

HandoverRow.propTypes = {
    row: PropTypes.object.isRequired,
    status: PropTypes.string.isRequired,
    check: PropTypes.object,
    duplicateOf: PropTypes.number,
    codeChecks: PropTypes.object,
    disabled: PropTypes.bool,
    onPreview: PropTypes.func.isRequired,
    onPickCode: PropTypes.func.isRequired,
    onSetName: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
}

HandoverRow.defaultProps = {
    check: null,
    duplicateOf: null,
    codeChecks: {},
    disabled: false,
}

export default React.memo(HandoverRow)
